import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { postForm, readCredits, WEBHOOKS } from "../lib/webhooks";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

const MODELS = [
  { id: "seedream45",   label: "Baja",  meta: "Seedream 4.5",    base: 10, resolutions: [{id:"2k",label:"2K",mult:1},{id:"4k",label:"4K",mult:1}] },
  { id: "seedream5pro", label: "Media", meta: "Seedream 5 Pro",  base: 25, resolutions: [{id:"1k",label:"1K",mult:1},{id:"2k",label:"2K",mult:1.5}] },
  { id: "nano",         label: "Alta",  meta: "Nano Banana Pro", base: 50, resolutions: [{id:"1k",label:"1K",mult:1},{id:"2k",label:"2K",mult:1},{id:"4k",label:"4K",mult:1.5}] },
];
const FORMATS = [
  { id: "3:4", label: "3:4 · Instagram", w: 3, h: 4 },
  { id: "1:1", label: "1:1 · Cuadrado",  w: 1, h: 1 },
  { id: "9:16",label: "9:16 · Story",    w: 9, h: 16 },
];
const GENDERS = ["Mujer", "Hombre", "Unisex"];

const HAIR_OPTIONS = {
  Mujer: ["", "Suelto", "Recogido", "Liso", "Ondulado"],
  Hombre: ["", "Rapado", "Corto", "Medio", "Largo"],
  Unisex: ["", "Corto", "Largo", "Recogido"],
};

function computeCost(m, resId) {
  const r = m.resolutions.find((x) => x.id === resId) || m.resolutions[0];
  return Math.ceil(m.base * r.mult);
}

function HelpPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-1.5 rounded-full border border-white/12 hover:border-[rgb(var(--brand))]/60 bg-white/[0.03] hover:bg-[rgb(var(--brand))]/10 px-2.5 py-[3px] text-[10px] font-mono uppercase tracking-[0.2em] text-[rgb(var(--paper-dim))] hover:text-[rgb(var(--paper))] transition-colors"
          data-testid="motor-help-btn"
          aria-label="¿No sabes qué motor elegir?"
        >
          <span className="grid place-items-center h-3 w-3 rounded-full border border-current text-[9px] leading-none">?</span>
          ¿Cuál elegir?
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={10} className="w-[min(92vw,400px)] p-0 border-white/10 bg-transparent shadow-2xl">
        <div
          className="rounded-xl overflow-hidden border border-white/10"
          style={{ background: "linear-gradient(180deg, rgba(15,20,30,0.96), rgba(6,9,14,0.96))", backdropFilter: "blur(20px)" }}
          data-testid="motor-help-popover"
        >
          <div className="px-5 pt-5 pb-4 border-b border-white/10">
            <span className="tag" style={{ borderColor: "rgba(92,225,230,0.4)", color: "rgb(var(--brand))" }}>Guía · motor</span>
            <h4 className="mt-3 font-display text-xl leading-tight text-[rgb(var(--paper))] font-medium">
              ¿No sabes cuál <span className="italic font-serif" style={{ color: "rgb(var(--brand))" }}>elegir?</span>
            </h4>
          </div>
          <div className="px-5 py-5 space-y-4">
            <p className="text-[13px] leading-relaxed text-[rgb(var(--paper-dim))]">
              Empieza por <span className="text-[rgb(var(--paper))] font-medium">Seedream 5 Pro</span>.
              Es la opción más equilibrada para el 80% de los casos: calidad
              profesional, coste razonable y resultados consistentes lote a lote.
            </p>
            <div className="rounded-lg border border-white/8 divide-y divide-white/8">
              <div className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))]">Baja · 10c</span>
                  <span className="text-[13px] font-medium text-[rgb(var(--paper))]">Seedream 4.5</span>
                </div>
                <p className="mt-1 text-[12px] text-[rgb(var(--paper-dim))] leading-relaxed">
                  Rápido y económico. Ideal para iterar, testear composiciones o generar volumen con bajo consumo.
                </p>
              </div>
              <div className="px-4 py-3 bg-[rgb(var(--brand))]/[0.05]">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "rgb(var(--brand))" }}>Media · 25c · recomendado</span>
                </div>
                <div className="text-[13px] font-medium text-[rgb(var(--paper))] mt-0.5">Seedream 5 Pro</div>
                <p className="mt-1 text-[12px] text-[rgb(var(--paper-dim))] leading-relaxed">
                  El equilibrio perfecto. Pensado para ecommerce, uso habitual y campañas donde importa la coherencia.
                </p>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))]">Alta · 50c</span>
                  <span className="text-[13px] font-medium text-[rgb(var(--paper))]">Nano Banana Pro</span>
                </div>
                <p className="mt-1 text-[12px] text-[rgb(var(--paper-dim))] leading-relaxed">
                  Máxima fidelidad de textil y luz. Para catálogos premium, editorial y producto de alto ticket.
                </p>
              </div>
            </div>
            <p className="text-[12px] leading-relaxed text-[rgb(var(--paper-dim))]">
              ¿Quieres el mejor resultado posible? Usa <span className="text-[rgb(var(--paper))] font-medium">Nano Banana Pro</span> — listo para PDP, lookbook y campaña impresa.
            </p>
            <div className="flex items-start gap-2 rounded-lg bg-white/[0.04] border border-white/8 px-3 py-2.5">
              <span className="text-[rgb(var(--brand))] text-sm leading-none mt-0.5">◆</span>
              <p className="text-[11px] leading-relaxed text-[rgb(var(--paper-dim))]">
                Puedes cambiar de motor en cualquier momento. El coste en créditos se recalcula al vuelo.
              </p>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BeforeAfter({
  before,
  after,
  background,
  customBg
}) {

  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const bgSrc = customBg
    ? URL.createObjectURL(customBg)
    : background;

  const onMove = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    let p = ((clientX - r.left) / r.width) * 100;
    p = Math.max(4, Math.min(96, p));
    setPos(p);
  };

  const downloadImage = () => {
    if (!after) return;

    const secureUrl = after.replace(/^http:\/\//, "https://");

    // Añade fl_attachment a la URL de Cloudinary
    const downloadUrl = secureUrl.replace(
      "/image/upload/",
      "/image/upload/fl_attachment/"
    );

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div
      ref={ref}
      onPointerDown={(e)=>{
        dragging.current = true;
        onMove(e.clientX);
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e)=>{
        if(dragging.current){
          onMove(e.clientX);
        }
      }}
      onPointerUp={()=> dragging.current = false}
      className="relative aspect-[3/4] w-full rounded-xl overflow-hidden bg-[#080b12] cursor-ew-resize select-none"
    >

      {/* 🧠 SOLO SI HAY IMAGEN */}
      {!before && null}

      {/* 🔥 ESTADO GENERANDO */}
      {!after && before && (
        <>
          {/* fondo SOLO en generating */}
          {bgSrc && (
            <img
              src={bgSrc}
              alt="background"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* overlay */}
          {bgSrc && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          )}

          <div className="absolute inset-0 flex items-center justify-center">

            <div className="absolute h-72 w-72 rounded-full bg-[rgb(var(--brand))] blur-3xl animate-breathe" />

            <div className="relative flex flex-col items-center gap-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[rgb(var(--brand))]">
                AI PROCESSING
              </span>

              <h3 className="font-display text-3xl font-light text-white">
                Generando
                <span className="italic font-serif text-[rgb(var(--brand))]">...</span>
              </h3>

              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                Creando modelo IA
              </p>
            </div>

          </div>
        </>
      )}

      {/* 🔥 RESULTADO */}
      {after && (
        <img
          src={after}
          alt="resultado"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* 🔥 ANTES */}
      {before && (
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath:`inset(0 ${100-pos}% 0 0)` }}
        >
          <img
            src={before}
            alt="original"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      )}

      {/* 🔥 SEPARADOR (SLIDER) */}
        {before && (
          <div
            className="absolute inset-y-0 z-20 pointer-events-none"
            style={{ left: `${pos}%` }}
          >
            {/* línea */}
            <div className="w-[2px] h-full bg-[rgb(var(--brand))]" />

            {/* handle */}
            <div
              className="
                absolute
                top-1/2
                left-0
                -translate-x-1/2
                -translate-y-1/2
                h-10
                w-10
                rounded-full
                bg-[rgb(var(--brand))]
                text-black
                grid
                place-items-center
                font-bold
                shadow-lg
              "
            >
              ⇔
            </div>
          </div>
        )}

      {/* etiquetas */}
      {before && (
        <div className="absolute top-3 left-3 z-20 bg-black/60 text-white px-2 py-1 rounded text-[10px] uppercase">
          Antes
        </div>
      )}

      {after && (
        <div className="absolute top-3 right-3 z-20 bg-[rgb(var(--brand))] text-black px-2 py-1 rounded text-[10px] uppercase">
          Después
        </div>
      )}

      {/* slider SOLO si hay after */}
      {after && (
        <div
          className="absolute inset-y-0 z-20 pointer-events-none"
          style={{ left:`${pos}%` }}
        >
          <div className="w-[3px] h-full bg-[rgb(var(--brand))]" />
          <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-[rgb(var(--brand))] text-black grid place-items-center font-bold">
            ⇔
          </div>
        </div>
      )}

       {after && (
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            downloadImage();
          }}
          className="absolute bottom-4 right-4 z-30 inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--brand))] px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-black shadow-lg transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white/80"
          aria-label="Descargar imagen generada"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
          Descargar
        </button>
      )}
    </div>
  );
}

export function Studio({ user, token, credits, onNeedAuth, onGenerated }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreview] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [sliderPosition, setSliderPosition] = useState(50);
  const [model, setModel] = useState(MODELS[0].id);
  const [resolution, setResolution] = useState(MODELS[0].resolutions[0].id);
  const [format, setFormat] = useState(FORMATS[0].id);
  const [gender, setGender] = useState("Mujer");
  const [hair, setHair] = useState("");
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [activeCategory, setActiveCategory] = useState("zara");
  const [bgPanelOpen, setBgPanelOpen] = useState(false);
  const [customBg, setCustomBg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const dropRef = useRef(null);

  const BACKGROUNDS = {
    zara: [
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785931009/image_1785703222391_9mivfy_faytxh.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785931009/image_1785700474627_kpys1t_i8ekrm.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785931012/image_1785703515127_dy5my5_rb8ez7.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785931016/image_1785703646630_rar2ps_zmhuqg.png"
    ],
    streetwear: [
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930975/image_1785764471456_8l7ctj_fkntyu.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930981/image_1785768350910_geqwr0_ustn9t.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930988/image_1785765033685_btceel_o7m2fs.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930994/image_1785768709814_irpxd1_m512zc.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785931609/image_1785764336011_hie2tq-min_qrj1ad.jpg",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785931610/image_1785706496514_4qe0rz-min_aeespr.jpg"
    ],
    sport: [
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930927/3d40859ac50a3a434f53c578d32f950a_1_1785704032_1253_mrafkq.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930933/image_1785704418166_7fjugo_l2whbu.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930940/df77ddabfb17bbae4dc75b0d6e55b39e_1_1785704230_1165_hnxd6q.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930940/image_1785766860844_oause3_salyqc.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930944/image_1785766480544_a7przc_litmcb.png",  
    ],
    lujo: [
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930861/image_1785769275998_8mgzib_fymsam.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930874/image_1785769111699_qo3ikv_km2h2a.png",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1785930889/image_1785768913071_40twns_qzjdg0.png"
    ],
    casual: [
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1787227844/image_c83971636e012789077a14c98bf9a404_jyrdj5.jpg",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1787227843/image_2e11dc75eddde54dd7af5c2f9ad244e3_wfndtp.jpg",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1787227842/casual_1_tppdth.jpg",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1787227841/fondo_casual_1_2_f8jeyd.jpg",
      "https://res.cloudinary.com/dcyd4lgd6/image/upload/v1787227841/fono_casual_2_1_eytgzs.jpg",  
    ],
  };
  const CATEGORIES = [
    "clean",
    "streetwear",
    "sport",
    "lujo",
    "casual",
    "custom"
  ];

  const modelObj = MODELS.find((m) => m.id === model) || MODELS[0];

  useEffect(() => {
    if (!modelObj.resolutions.find((r) => r.id === resolution)) {
      setResolution(modelObj.resolutions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const cost = computeCost(modelObj, resolution);
  
  useEffect(() => {
    if (!file) return setPreview("");
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) setFile(f);
  };

  const generate = async () => {
    if (!file) return setStatus("Sube una imagen primero.");
    if (!user) return onNeedAuth?.();
    setStatus("");
    setLoading(true);
    const fd = new FormData();
    fd.append("image", file);
    fd.append("model", model);
    fd.append("resolution", resolution);
    fd.append("format", format);
    fd.append("gender", gender.toLowerCase());
    fd.append("hair", hair);
    if (customBg) {
      fd.append("backgroundFile", customBg); // archivo real
    } else if (selectedBackground) {
      fd.append("background", selectedBackground); // URL
    }
    fd.append("cost", String(cost));
    try {
      const r = await postForm(WEBHOOKS.generate, fd, token);

      // 🔴 SI HAY ERROR → NO GENERAR IMAGEN
      if (r?.error) {
        const sinCreditos = r.code === "INSUFFICIENT_CREDITS";

        setStatus(
          r.message ||
          (sinCreditos
            ? "No tienes créditos suficientes."
            : "No se pudo generar la imagen. Inténtalo de nuevo.")
        );

        onGenerated?.({
          error: true,
          credits: typeof r.credits === "number" ? r.credits : undefined,
        });

        // Solo redirigir si realmente no quedan créditos
        if (sinCreditos) {
          setTimeout(() => {
            const el = document.getElementById("precios");

            if (el) {
              el.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 1500);
        }

        return;
      }

      // 🟢 ÉXITO
      const creditsFromBackend =
        typeof r?.newCredits === "number"
          ? r.newCredits
          : readCredits(r).credits;

      if (r?.imageUrl) {
        setGeneratedUrl(r.imageUrl);
      }

      onGenerated?.({
        id: Date.now(),
        url: r?.imageUrl || previewUrl,
        model,
        resolution,
        format,
        cost,
        newCredits: creditsFromBackend,
        createdAt: new Date().toISOString(),
      });

      setStatus("Imagen generada.");
    } catch (e) {
      setStatus(e?.message || "Error al generar la imagen.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = FORMATS.find((f) => f.id === format);
  const previewAspect = `${fmt.w}/${fmt.h}`;

  return (
    <section id="studio" className="relative px-5 md:px-8 py-24 md:py-32">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex items-end justify-between mb-10 md:mb-14 gap-6 flex-wrap">
          <div>
            <span className="tag mb-4 inline-flex">
              § 004 · Studio
              {user && (
                credits === null ? (
                  <span className="ml-2 text-red-400" data-testid="studio-credits-error">· créditos: error</span>
                ) : (
                  <span className="ml-2">· {credits ?? 0} créditos</span>
                )
              )}
            </span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-[8vw] md:text-[4.6vw] leading-[0.95] tracking-[-0.02em] text-[rgb(var(--paper))] max-w-3xl font-light"
            >
              El <span className="italic font-serif text-[rgb(var(--brand))]">studio.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6 items-stretch">
          {/* Controls */}
          <div className="lg:col-span-5 card-tech p-6 md:p-7 flex flex-col gap-5" data-testid="studio-controls">
            {/* console header */}
            <div className="flex items-center justify-between border-b border-white/8 pb-4">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-white/20" />
                <span className="h-2 w-2 rounded-full bg-white/20" />
                <span className="h-2 w-2 rounded-full bg-[rgb(var(--brand))] glow-cyan" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[rgb(var(--paper-dim))]">
                Nexeron Studio
              </span>
            </div>

            {/* upload */}
            <div
              ref={dropRef}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="relative border border-dashed border-white/15 rounded-xl p-5 hover:border-[rgb(var(--brand))]/60 transition-colors"
            >
              <input
                id="fileInput"
                type="file"
                accept="image/*"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="upload-input"
              />
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-[rgb(var(--brand))]/8 border border-[rgb(var(--brand))]/25 grid place-items-center text-[rgb(var(--brand))]">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 12V3M9 3L5 7M9 3L13 7M3 12v3h12v-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div>
                  <div className="font-medium text-[rgb(var(--paper))]">
                    {file ? file.name : "Arrastra o selecciona una prenda"}
                  </div>
                  <div className="text-xs text-[rgb(var(--paper-dim))]">JPG / PNG · hasta 12MB</div>
                </div>
              </div>
            </div>

            {/* motor */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))] mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2"><span>Motor</span><HelpPopover /></span>
                <span className="text-[rgb(var(--paper-dim))]/60">calidad ↑</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setModel(m.id)}
                    data-testid={`model-${m.id}`}
                    className={`rounded-lg border px-3 py-3 text-left transition-all ${
                      model === m.id
                        ? "border-[rgb(var(--brand))]/60 bg-[rgb(var(--brand))]/8 glow-cyan"
                        : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div className="text-sm font-medium text-[rgb(var(--paper))]">{m.label}</div>
                    <div className="font-mono text-[10px] uppercase text-[rgb(var(--paper-dim))] mt-0.5">
                      {m.meta} · {m.base}c
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* resolution */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))] mb-3 flex items-center justify-between">
                <span>Resolución</span>
                <span className="text-[rgb(var(--paper-dim))]/60">{modelObj.meta}</span>
              </div>
              <div className="flex gap-2">
                {modelObj.resolutions.map((r) => {
                  const rCost = Math.ceil(modelObj.base * r.mult);
                  const active = resolution === r.id;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setResolution(r.id)}
                      data-testid={`res-${r.id}`}
                      className={`flex-1 rounded-lg border px-3 py-3 text-left transition-all ${
                        active ? "border-[rgb(var(--brand))]/60 bg-[rgb(var(--brand))]/8" : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <div className="text-sm font-medium text-[rgb(var(--paper))]">
                        {r.label}
                        {r.mult > 1 && (
                          <span className="ml-2 font-mono text-[10px] uppercase" style={{ color: "rgb(var(--brand))" }}>+50%</span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] uppercase text-[rgb(var(--paper-dim))] mt-0.5">{rCost}c</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* format + gender */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))] mb-2">Formato</div>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[rgb(var(--paper))] focus:border-[rgb(var(--brand))] outline-none"
                  data-testid="format-select"
                >
                  {FORMATS.map((f) => <option key={f.id} value={f.id} className="bg-[rgb(var(--ink))]">{f.label}</option>)}
                </select>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))] mb-2">Género</div>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[rgb(var(--paper))] focus:border-[rgb(var(--brand))] outline-none"
                  data-testid="gender-select"
                >
                  {GENDERS.map((g) => <option key={g} value={g} className="bg-[rgb(var(--ink))]">{g}</option>)}
                </select>
              </div>
            </div>

            {/* hair */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))] mb-2">
                Pelo (opcional)
              </div>

              <div className="flex gap-2 flex-wrap">
                {HAIR_OPTIONS[gender].map((h) => (
                  <button
                    key={h || "auto"}
                    onClick={() => setHair(h)}
                    className={`rounded-full px-4 py-1.5 text-xs border transition-colors ${
                      hair === h
                        ? "border-[rgb(var(--brand))] text-[rgb(var(--brand))]"
                        : "border-white/10 text-[rgb(var(--paper-dim))]"
                    }`}
                    data-testid={`hair-${h || "auto"}`}
                  >
                    {h || "Auto"}
                  </button>
                ))}
              </div>
            </div>

            {/* BACKGROUNDS */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))] mb-2">
                  Fondo (opcional)
                </div>

                <button
                  onClick={() => setBgPanelOpen(true)}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm hover:border-[rgb(var(--brand))]/60 transition-colors"
                >
                  Abrir biblioteca
                </button>

                {/* 🔥 AQUÍ VA EL BOTÓN QUITAR */}
                {selectedBackground && (
                  <div className="flex items-center gap-2 mt-3">
                    
                    <span className="text-xs text-[rgb(var(--brand))]">
                      Fondo seleccionado
                    </span>

                    <button
                      onClick={() => {
                        setSelectedBackground(null);
                        setCustomBg(null);
                      }}
                      className="text-xs text-red-400 hover:underline"
                    >
                      quitar
                    </button>

                  </div>
                )}

              </div>

              {bgPanelOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center">

                  <div className="w-[90vw] max-w-5xl bg-[#0b0f17] border border-white/10 rounded-xl p-6">

                    {/* HEADER */}
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg text-white font-medium">Seleccionar fondo</h3>
                      <button
                        onClick={() => setBgPanelOpen(false)}
                        className="text-white/50 hover:text-white"
                      >
                        ✕
                      </button>
                    </div>

                    {/* CATEGORÍAS */}
                    <div className="flex gap-3 mb-6 flex-wrap">
                      {["zara", "streetwear", "sport", "lujo","casual", "custom"].map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setActiveCategory(cat)}
                          className={`px-3 py-1.5 rounded-full text-xs border ${
                            activeCategory === cat
                              ? "border-[rgb(var(--brand))] text-[rgb(var(--brand))]"
                              : "border-white/10 text-white/50"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>

                    {/* CONTENIDO */}
                    {activeCategory === "custom" ? (
                      <div className="flex flex-col items-center justify-center h-[40vh]">

                        <input
                          id="bgUpload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setCustomBg(file); // 🔥 guardar archivo real
                              setSelectedBackground(URL.createObjectURL(file)); // preview
                              setBgPanelOpen(false);
                            }
                          }}
                        />

                        <button
                          onClick={() => document.getElementById("bgUpload").click()}
                          className="rounded-xl border border-[rgb(var(--brand))]/40 px-6 py-4 text-sm text-[rgb(var(--brand))] hover:bg-[rgb(var(--brand))]/10 transition"
                        >
                          Subir tu propio fondo
                        </button>

                        <p className="mt-4 text-xs text-white/40">
                          Usa tu propia imagen como fondo
                        </p>

                      </div>
                    ) : (
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">

                        {BACKGROUNDS[activeCategory].map((bg, i) => (
                          <img
                            key={i}
                            src={bg}
                            onClick={() => {
                              setSelectedBackground(bg);
                              setCustomBg(null); // 🔥 importante
                              setBgPanelOpen(false);
                            }}
                            className="cursor-pointer rounded-lg border border-white/10 hover:scale-105 transition"
                          />
                        ))}

                      </div>
                    )}

                  </div>
                </div>
              )}

            <button
              onClick={generate}
              disabled={loading}
              className="group relative rounded-full text-[rgb(var(--ink))] px-6 py-4 text-sm font-medium overflow-hidden disabled:opacity-60"
              data-testid="generate-btn"
            >
              <span className="absolute inset-0 bg-[rgb(var(--brand))] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:bg-[rgb(var(--paper))]" />
              <span className="relative flex items-center justify-center gap-3">
                {loading ? (
                  <>
                    <span className="inline-block h-3 w-3 rounded-full bg-black animate-pulse" />
                    Generando…
                  </>
                ) : (
                  <>
                    <span>Generar</span>
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-black/15 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider"
                      data-testid="generate-cost"
                    >
                      {cost}c
                    </span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform group-hover:translate-x-0.5"><path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </>
                )}
              </span>
            </button>

           {status && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className={`mt-2 rounded-lg border px-4 py-3 text-sm font-mono relative overflow-hidden ${
                status.toLowerCase().includes("créditos")
                  ? "border-red-500/30 bg-red-500/10 text-red-400"
                  : "border-[rgb(var(--brand))]/20 bg-[rgb(var(--brand))]/10 text-[rgb(var(--brand))]"
              }`}
              data-testid="studio-status"
            >
              {/* GLOW ANIMADO */}
              {status.toLowerCase().includes("créditos") && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 rounded-lg border border-red-500/30 animate-borderPulse" />
                </div>
              )}

              <div className="flex items-center gap-2 relative z-10">
                
                {/* PUNTO */}
                <span
                  className={`h-2 w-2 rounded-full ${
                    status.toLowerCase().includes("créditos")
                      ? "bg-red-400 animate-pulse"
                      : "bg-[rgb(var(--brand))] animate-pulse"
                  }`}
                />

                {/* TEXTO */}
                <span>{status}</span>
              </div>
            </motion.div>
          )}
            <p className="text-[11px] text-[rgb(var(--paper-dim))]/70 leading-relaxed">
             Tus imágenes solo se usan para generar tu resultado.
            </p>
          </div>

          {/* Preview */}
          <div className="lg:col-span-7">
            <div
              className="
                relative
                w-full
                rounded-xl
                overflow-hidden
                card-tech
                scanline
              "
              style={{
                aspectRatio: "3 / 4"
              }}
              onMouseMove={(e) => {
                const t = e.currentTarget;
                const r = t.getBoundingClientRect();

                t.style.setProperty("--mx", `${e.clientX - r.left}px`);
                t.style.setProperty("--my", `${e.clientY - r.top}px`);
              }}
              data-testid="studio-preview"
            >

              {previewUrl ? (
                <>
                  <BeforeAfter
                    before={previewUrl}
                    after={generatedUrl}
                    background={selectedBackground}
                    customBg={customBg}
                  />

                  {/* HUD info solo con imagen */}
                  <div
                    className="
                      absolute
                      top-4
                      left-6
                      right-6

                      flex
                      items-center
                      justify-between

                      font-mono
                      text-[10px]
                      uppercase
                      tracking-[0.22em]

                      text-white/80
                    "
                  >

                    <span>
                      {format} · {modelObj.meta} · {resolution.toUpperCase()}
                    </span>


                    <span
                      className={
                        loading
                        ? "text-[rgb(var(--brand))]"
                        : ""
                      }
                    >

                      <span
                        className="
                          h-1.5
                          w-1.5
                          rounded-full
                          bg-[rgb(var(--brand))]
                          inline-block
                          mr-1
                          animate-pulse
                        "
                      />

                      {loading ? "processing" : "ready"}

                    </span>

                  </div>
                </>
              ) : (

               <div
                className="
                  absolute
                  inset-0
                  flex
                  flex-col
                  items-center
                  justify-center
                  text-center
                  px-8
                "
                style={{
                  top: "50%",
                  transform: "translateY(-50%)"
                }}
              >

                  {/* Icono */}
                  <div
                    className="
                      w-16
                      h-16
                      rounded-2xl
                      border
                      border-white/10
                      bg-white/[0.03]
                      flex
                      items-center
                      justify-center
                      mb-5
                    "
                  >

                    <svg
                      width="34"
                      height="34"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="text-[rgb(var(--brand))]"
                    >
                      <path d="M12 5v14"/>
                      <path d="M5 12h14"/>
                    </svg>

                  </div>


                  {/* Título */}
                  <h3
                    className="
                      font-display
                      text-3xl
                      font-light
                      text-[rgb(var(--paper))]
                    "
                  >
                    Sube una{" "}
                    <span
                      className="
                        italic
                        font-serif
                        text-[rgb(var(--brand))]
                      "
                    >
                      prenda.
                    </span>
                  </h3>


                  {/* Descripción */}
                  <p
                    className="
                      mt-4
                      max-w-xs
                      text-sm
                      leading-relaxed
                      text-[rgb(var(--paper-dim))]
                    "
                  >
                    Convierte una foto de móvil en una imagen profesional con modelo IA.
                  </p>


                  {/* Estado */}
                  <div
                    className="
                      mt-8
                      rounded-full
                      border
                      border-white/10
                      px-5
                      py-2

                      font-mono
                      text-[10px]
                      uppercase
                      tracking-[0.25em]

                      text-white/50
                    "
                  >
                    Esperando imagen
                  </div>


                  {/* Ayuda */}
                  <div
                    className="
                      mt-5
                      font-mono
                      text-[10px]
                      uppercase
                      tracking-[0.25em]
                      text-white/30
                    "
                  >
                    Arrastra o selecciona una foto
                  </div>


                </div>

              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
